import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  DialogActions,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { blue, red } from '@mui/material/colors';
import {
  Hail,
  HourglassBottom,
  MonetizationOnSharp,
} from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import GenericTable from '../../generic-table/generic-table';
import CustomerWithNavigate from '../../customer-with-navigate/customer-with-navigate';
import OrderNotes from '../../order-notes/order-notes';
import { showDeliveryState } from '../../styles/order-states.style';
import { showWorkflow } from '../../styles/workflow-display';
import LinkIcon from '@mui/icons-material/Link';
import CopyLinkButton from '../../copy-link-button/copy-link-button';
import { format } from 'date-fns';
import CustomDialog from '../../custom-dialog/custom-dialog';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import { IconWithText } from '../../styles/components';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import AlertCard from './alert-card';
import { categorizeOrderAlerts, categorizePickupAlerts } from './utils';
import OrdersDialog from './orders-dialog';
import PickupsDialog from './pickups-dialog';

export enum AlertType {
  UNDELIVERED = 'UNDELIVERED',
  UNPAID = 'UNPAID',
  INCOMPLETE_PICKUPS = 'INCOMPLETE_PICKUPS',
}

export interface AlertsData {
  UNDELIVERED: OrderType[];
  UNPAID: OrderType[];
  INCOMPLETE_PICKUPS: IPickupTask[];
}

function Alerts() {
  const { user, config } = useContext(AuthContext);

  const { showMessage } = useSnackBar();

  const [alertsData, setAlertsData] = useState<AlertsData>();
  const [selectedType, setSelectedType] = useState<
    | AlertType.UNDELIVERED
    | AlertType.UNPAID
    | 'RECENT_PICKUPS'
    | 'LATE_PICKUPS'
    | null
  >(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/alerts/`;
        setAlertsData((await axios.get<AlertsData>(endpoint, config)).data);
      } catch {
        showMessage('Failed to get alerts', 'error');
      }
    };
    fetchAlerts();
  }, []);

  const handleCardClick = (type: typeof selectedType) => {
    setSelectedType(type);
  };

  const { active_orders, snoozed_orders } = categorizeOrderAlerts(alertsData);
  const { recent_pickups, late_pickups } = categorizePickupAlerts(alertsData);

  return alertsData ? (
    <>
      <Grid2 container spacing={2}>
        <Grid2 xs={4} sx={{ display: 'flex' }}>
          <AlertCard
            background={red[active_orders.UNDELIVERED.length > 0 ? 300 : 50]}
            icon={<HourglassBottom />}
            label={'Late Orders'}
            onClick={() => handleCardClick(AlertType.UNDELIVERED)}
          >
            <Typography variant="body2" fontWeight="bold">
              {alertsData.UNDELIVERED.length == 0 && '0'}
              {active_orders.UNDELIVERED.length > 0 &&
                `${active_orders.UNDELIVERED.length} active`}
              <br />
              {snoozed_orders.UNDELIVERED.length > 0 &&
                `${snoozed_orders.UNDELIVERED.length} muted`}
            </Typography>
          </AlertCard>
        </Grid2>

        <Grid2 xs={4} sx={{ display: 'flex' }}>
          <AlertCard
            background={red[active_orders.UNPAID.length > 0 ? 300 : 50]}
            icon={<MonetizationOnSharp />}
            label={'Unpaid Orders'}
            onClick={() => handleCardClick(AlertType.UNPAID)}
          >
            <Typography variant="body2" fontWeight="bold">
              {alertsData.UNPAID.length == 0 && '0'}
              {active_orders.UNPAID.length > 0 &&
                `${active_orders.UNPAID.length} active`}
              <br />
              {snoozed_orders.UNPAID.length > 0 &&
                `${snoozed_orders.UNPAID.length} muted`}
            </Typography>
          </AlertCard>
        </Grid2>

        <Grid2 xs={4} sx={{ display: 'flex' }}>
          <AlertCard
            background={red[late_pickups.length > 0 ? 300 : 50]}
            icon={<HourglassBottom />}
            label={'Late Pickups'}
            onClick={() => handleCardClick('LATE_PICKUPS')}
          >
            <Typography variant="body2" fontWeight="bold">
              {late_pickups.length}
            </Typography>
          </AlertCard>
        </Grid2>
        <Grid2 xs={4} sx={{ display: 'flex' }}>
          <AlertCard
            background={red[recent_pickups.length > 0 ? 300 : 50]}
            icon={<Hail />}
            label={'Recent Pickups'}
            onClick={() => handleCardClick('RECENT_PICKUPS')}
          >
            <Typography variant="body2" fontWeight="bold">
              {recent_pickups.length}
            </Typography>
          </AlertCard>
        </Grid2>
      </Grid2>

      {(selectedType == AlertType.UNDELIVERED ||
        selectedType == AlertType.UNPAID) && (
        <OrdersDialog
          onClose={() => setSelectedType(null)}
          selectedType={selectedType}
          alertsData={alertsData}
        />
      )}

      {selectedType == 'RECENT_PICKUPS' && (
        <PickupsDialog
          tasks={recent_pickups}
          onClose={() => setSelectedType(null)}
          title="Recent Pickups"
        />
      )}

      {selectedType == 'LATE_PICKUPS' && (
        <PickupsDialog
          tasks={late_pickups}
          onClose={() => setSelectedType(null)}
          title="Late Pickups"
        />
      )}
    </>
  ) : (
    <CircularProgress />
  );
}

export default Alerts;
